/**
 * Revenue Protection — consola comercial de pérdidas aparentes.
 *
 * Foco del Gerente/Analista Comercial: fraude, submedición y
 * recuperación de facturación.  Separa las pérdidas COMERCIALES
 * (aparentes) de las físicas y prioriza por recuperación esperada
 * (precision@K, no accuracy), alineado con el caso de uso "Score de
 * ilícitos / Revenue Protection" del análisis ML sanitario.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { PriorityCase, OperationalInsight } from "../api/mock";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";

const COMERCIAL_TIPOS = new Set(["fraude_comercial", "medidor_falla"]);

function clp(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `CLP ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `CLP ${(n / 1_000).toFixed(0)}k`;
  return `CLP ${n.toFixed(0)}`;
}

export function CommercialView(): JSX.Element {
  const casosQuery = useQuery<PriorityCase[]>({
    queryKey: ["casos"],
    queryFn: () => api<PriorityCase[]>("/casos"),
    staleTime: 30_000,
  });
  const insightsQuery = useQuery<OperationalInsight[]>({
    queryKey: ["insights"],
    queryFn: () => api<OperationalInsight[]>("/insights"),
    staleTime: 60_000,
  });

  // Casos comerciales (fraude + submedición), ranqueados por recuperación esperada.
  const comerciales = useMemo(() => {
    const all = casosQuery.data ?? [];
    return all
      .filter((c) => COMERCIAL_TIPOS.has(c.tipo))
      .map((c) => ({
        ...c,
        recuperable_clp: Math.round(c.perdida_estimada_clp_mes * c.prob_recuperacion),
      }))
      .sort((a, b) => b.recuperable_clp - a.recuperable_clp);
  }, [casosQuery.data]);

  const insightsFraude = useMemo(
    () => (insightsQuery.data ?? []).filter((i) => i.categoria === "fraude"),
    [insightsQuery.data],
  );

  const totalRecuperable = comerciales.reduce((a, c) => a + c.recuperable_clp, 0);
  const totalPerdida = comerciales.reduce((a, c) => a + c.perdida_estimada_clp_mes, 0);
  const confianzaMedia =
    comerciales.length > 0
      ? Math.round(
          (comerciales.reduce((a, c) => a + c.confianza_modelo, 0) /
            comerciales.length) *
            100,
        )
      : 0;

  return (
    <>
      <PageHeader
        eyebrow="Revenue Protection"
        title="Pérdidas Comerciales"
        subtitle="Fraude, submedición y recuperación de facturación, priorizados por recuperación esperada por inspección."
        actions={
          <Link to="/casos" className="btn btn--primary">
            <Icon name="flask" size={13} /> Ver todos los casos
          </Link>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Pérdida aparente detectada</span>
          <span className="kpi__value">{clp(totalPerdida)}</span>
          <span className="kpi__hint">por mes, casos comerciales</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Recuperable estimado</span>
          <span className="kpi__value">{clp(totalRecuperable)}</span>
          <span className="kpi__hint">ponderado por prob. de éxito</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Casos comerciales</span>
          <span className="kpi__value">{comerciales.length}</span>
          <span className="kpi__hint">fraude + submedición</span>
        </div>
        <div className="kpi kpi--magenta">
          <span className="kpi__label">Confianza media</span>
          <span className="kpi__value">{confianzaMedia}%</span>
          <span className="kpi__hint">score del modelo</span>
        </div>
      </div>

      {insightsFraude.length > 0 && (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card__title">Señales de fraude detectadas</div>
          <div className="vstack" style={{ gap: 8 }}>
            {insightsFraude.map((ins) => (
              <div
                key={ins.insight_id}
                className="status-block status-block--alert"
                style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}
              >
                <div className="hstack" style={{ gap: 8 }}>
                  <span className={`sev-pill sev-pill--${ins.severidad}`}>{ins.severidad}</span>
                  <strong>{ins.titulo}</strong>
                </div>
                <span className="text-muted" style={{ fontSize: 12.5 }}>{ins.detalle}</span>
                <span style={{ fontSize: 12.5 }}>{ins.recomendacion}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__title">
          <span>Cola priorizada de inspección comercial</span>
          <span className="text-faint text-mono" style={{ fontSize: 11 }}>
            por recuperación esperada
          </span>
        </div>
        <div className="table-wrapper" style={{ borderColor: "transparent" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Caso</th>
                <th>Tipo</th>
                <th>DMA</th>
                <th className="numeric">Pérdida/mes</th>
                <th className="numeric">Recuperable</th>
                <th className="numeric">Prob.</th>
                <th className="numeric">Score</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {comerciales.map((c) => (
                <tr key={c.caso_id}>
                  <td className="text-mono">{c.caso_id}</td>
                  <td>{c.tipo_label}</td>
                  <td>{c.dma_codigo}</td>
                  <td className="numeric">{clp(c.perdida_estimada_clp_mes)}</td>
                  <td className="numeric">
                    <span className="badge badge--accent">{clp(c.recuperable_clp)}</span>
                  </td>
                  <td className="numeric">{Math.round(c.prob_recuperacion * 100)}%</td>
                  <td className="numeric">
                    <span
                      className={`badge badge--${
                        c.ops_score >= 80 ? "danger" : c.ops_score >= 65 ? "warn" : "info"
                      }`}
                    >
                      {c.ops_score}
                    </span>
                  </td>
                  <td>
                    <Link to="/casos" className="btn btn--ghost btn--icon" aria-label="Abrir caso">
                      <Icon name="chevron-right" size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
              {comerciales.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted" style={{ textAlign: "center", padding: 24 }}>
                    Sin casos comerciales en la cola actual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
